'use client';

import Button from '@enlighten/design-system/dist/Button';
import Grid from '@enlighten/design-system/dist/Grid';
import Typography from '@enlighten/design-system/dist/Typography';
import type { StaticImageData } from 'next/image';
import Image from 'next/image';
import Link from 'next/link';
import type { ComponentProps } from 'react';
import { useState } from 'react';

import SpecialIcon from '@/assets/images/special.svg';

export interface TutorialCardProps {
	background: StaticImageData;
	avatar?: StaticImageData;
	title: string;
	subTitle: string;
	comment: string;
	linkProps: ComponentProps<typeof Link>;
}

export default function TutorialCard({ background, avatar, title, subTitle, comment, linkProps }: TutorialCardProps) {
	const [hover, setHover] = useState<boolean>(false);

	return (
		<Link
			{...linkProps}
			title={title}
			role="link"
			onMouseEnter={() => {
				setHover(true);
			}}
			onMouseLeave={() => {
				setHover(false);
			}}
		>
			<Grid
				position="relative"
				maxWidth={{ xs: '100%', md: 332 }}
				height={340}
				borderRadius={2}
				overflow="hidden"
				sx={{
					transition: 'box-shadow 0.4s ease',
					':hover': {
						boxShadow: '0 1px 2px -2px #00000029, 0 3px 6px 0 #0000001F, 0 5px 12px 4px #00000017',
						'.background': {
							filter: 'blur(40px)',
							opacity: 0.5,
							transform: 'scale(1.1)',
						},
					},
				}}
			>
				{hover ? (
					<Grid position="absolute" container direction="column" justifyContent="space-between" height="100%" padding={4} zIndex={99}>
						<Grid>
							{avatar && (
								<Grid
									position="relative"
									width={56}
									height={56}
									marginBottom={2}
									sx={{
										'& img': {
											width: 56,
											height: 56,
										},
										'.special': {
											position: 'absolute',
											right: 0,
											bottom: 0,
										},
									}}
								>
									<Image src={avatar.src} alt="avatar" width={56} height={56} />
									<SpecialIcon className="special" />
								</Grid>
							)}
							<Typography fontSize={16} textAlign="left" marginBottom="4px">
								{subTitle}
							</Typography>
							<Typography fontSize={20} fontWeight={700} lineHeight={1.5} textAlign="left">
								{comment}
							</Typography>
						</Grid>
						<Button fullWidth sx={{ height: 44, padding: '9px 15px' }}>
							더 알아보기
						</Button>
					</Grid>
				) : (
					<Grid position="absolute" padding={3} zIndex={99}>
						<Typography fontSize={22} fontWeight={700} textAlign="left" marginBottom={1}>
							{title}
						</Typography>
						<Typography fontSize={16} textAlign="left" marginBottom="4px">
							{subTitle}
						</Typography>
					</Grid>
				)}
				<Grid
					className="background"
					position="absolute"
					container
					justifyContent="center"
					width="100%"
					height="100%"
					sx={{
						background: `url(${background.src}) no-repeat center / cover`,
					}}
				/>
			</Grid>
		</Link>
	);
}
